import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText } from "@/components/icons";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { formatEuro, formatNlDate } from "@/components/Badges";
import { Page } from "@/components/PageConsole";
import { Card } from "@/components/ui/card";
import { InlineError, InlineEmpty } from "@/components/ListState";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUS_LABEL = { concept: "Concept", uitgegeven: "Uitgegeven", verstuurd: "Verstuurd" };

export default function FacturenList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try { const { data } = await api.get("/facturen"); setRows(data); }
    catch (e) { setError(getErrorMessage(e, "Kan facturen niet laden.")); }
  };
  useEffect(() => { load(); }, []);

  const facts = rows ? [`${rows.length} ${rows.length === 1 ? "factuur" : "facturen"}`] : [];

  return (
    <Page console={{
      variant: "view", title: "Facturen", facts,
      actions: [{ key: "nieuw", label: "Nieuwe factuur", variant: "primary", icon: FileText, onClick: () => navigate("/facturen/nieuw") }],
    }}>
      {error ? <InlineError testid="facturen-load-error" message={error} onRetry={load} /> :
        !rows ? <p className="text-body text-muted-foreground">Laden...</p> :
          rows.length === 0 ? (
            <InlineEmpty testid="facturen-empty" title="Nog geen facturen"
              purpose="Maak een factuur door een entiteit en een periode te kiezen; de werkbonnen staan al klaar met hun bedragen." />
          ) : (
            <>
            <Card className="hidden sm:block">
              <Table data-testid="facturen-table">
                <TableHeader><TableRow>
                  <TableHead>Nummer</TableHead><TableHead>Soort</TableHead><TableHead>Debiteur</TableHead>
                  <TableHead>Periode</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Totaal incl. btw</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((f) => (
                    <TableRow key={f.id} data-testid={`factuur-row-${f.id}`} className="cursor-pointer" onClick={() => navigate(`/facturen/${f.id}`)}>
                      <TableCell className="font-mono text-foreground">{f.factuur_nummer || "concept"}</TableCell>
                      <TableCell>{f.type === "creditnota" ? "Creditnota" : "Factuur"}</TableCell>
                      <TableCell className="text-foreground">{f.entity_name}</TableCell>
                      <TableCell className="text-muted-foreground">{formatNlDate(f.periode_van)} – {formatNlDate(f.periode_tot)}</TableCell>
                      <TableCell>{STATUS_LABEL[f.status] || f.status}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{f.totaal_incl_btw != null ? formatEuro(f.totaal_incl_btw) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <div className="space-y-2 sm:hidden" data-testid="facturen-cardlist">
              {rows.map((f) => (
                <Card key={f.id} data-testid={`factuur-card-${f.id}`} className="cursor-pointer p-4 transition-colors hover:bg-accent" onClick={() => navigate(`/facturen/${f.id}`)}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono text-foreground">{f.factuur_nummer || "concept"}</span>
                    <span className="font-mono tabular-nums text-foreground">{f.totaal_incl_btw != null ? formatEuro(f.totaal_incl_btw) : "—"}</span>
                  </div>
                  <div className="mt-1 text-body text-foreground">{f.entity_name}</div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-data text-muted-foreground">
                    <span>{f.type === "creditnota" ? "Creditnota" : "Factuur"}</span>
                    <span>{formatNlDate(f.periode_van)} – {formatNlDate(f.periode_tot)}</span>
                    <span>{STATUS_LABEL[f.status] || f.status}</span>
                  </div>
                </Card>
              ))}
            </div>
            </>
          )}
    </Page>
  );
}
